/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import Shimmer from 'react-shimmer-effect';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import SwapPropDropdownSAQC from './SwapPropDropdownSAQC';
import AddPropertyColumnComponent from './AddPropertyColumnComponent';
import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  focusNextPreSaveItem,
  focusNextPreSaveProgramItem,
  setPreSaveTableLocalState,
} from '../../ducks/calendarActions';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import TBportalTooltip from '../../../../shared/components/TBportalTooltip';
import Text from '../../../../shared/components/Text/Text';
import generateUniqueId from '../../../../shared/utils/generateUniqueId';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import { normalizeRepArray } from '../../../../shared/utils/normalizeRepToken';
import wipeRepKeywordOnMutation from '../../../../shared/utils/wipeRepKeywordOnMutation';

// Accepts: 55, 55.5, 55-60, 55.5-60.8, 5+, 4.5+
const VALUE_PATTERN = /^\d+(\.\d+)?(-\d+(\.\d+)?|\+)?$/;
const MAX_FIELD_VALUE = 10000;
const isValidRangeToken = (tok) => {
  if (!VALUE_PATTERN.test(tok)) return false;
  const parts = tok.replace('+', '').split('-');
  if (!parts.every((p) => parseFloat(p) <= MAX_FIELD_VALUE)) return false;
  if (parts.length === 2 && parseFloat(parts[0]) > parseFloat(parts[1])) return false;
  return true;
};
// For submit: pure numbers go as floats; range/+ tokens go as strings (backend accepts both)
const toFieldValue = (token) => {
  if (/^\d+(\.\d+)?$/.test(token)) return parseFloat(token);
  return token;
};

const FormWrapper = styled('form')`
  display: flex;
  width: 100%;
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: scroll;
`;
const PrimaryRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  margin-top: ${(props) => (props.isAdding ? '16px' : '0px')};
`;

const ValueRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const PrimaryRowValue = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  max-height: ${(props) => (props.isAdding && '24px')}; 
  margin-bottom: 3px;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: ${(props) => (!props.isError ? '1px solid #EEEEEE' : '1px solid red')};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : '#444444')};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : '#444444')};
  }
`;

const ShimmerRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  border-radius: 6px;
  color: transparent;
  margin-left: 5px;
  margin-right: 5px;
`;

const SetRowValue = styled('input')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: #FCFCFC;
  border: 1px solid #EEEEEE;
  text-align: center;
  border-radius: 4px;
`;

const ShimmerSetRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  margin-right: 5px;
`;

const SaveButtonContainer = styled('button')`
  display: flex;
  background: #10cd8c;
  height: 34px;
  padding-left: 8px;
  padding-right: 8px;
  font-family: 'Nunito Sans';
  color: ${(props) => (props.shimmer ? 'transparent' : 'white')};
  border-radius: 5px;
  font-weight: 700;
  font-size: 13px;
  align-items: center;
  min-width: 56px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  width: 100%;
`;

const RemoveIcon = styled('div')`
  padding: 4px 4px 4px 8px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RowsContainer = styled('div')`
  display: flex;
  width: 100%;
  align-items: flex-end;
  height: 40px;
  gap: 16px;
  margin-bottom: ${(props) => (props.hasError ? '12px' : '0px')};
`;

const ErrorAndRowsContainer = styled('div')`
display: flex;
flex-direction: column;
width: 100%;
`;

const RowValueContainer = styled('div')`
  flex: 1;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const SetRowValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  position: relative;
`;

const InfoIconWrapper = styled('div')`
  position: absolute;
  left: -20px;
  display: flex;
  align-items: center;
`;

const PrimaryValueContainer = styled('div')`
  flex: 1;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
  padding-top: ${(props) => (props.shimmer ? '0px' : '16px')};
`;

const SetPrimaryValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
  padding-top: ${(props) => (props.shimmer ? '0px' : '16px')};
`;

const TooltipDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
  min-width: 250px;
`;
const TooltipDataContainerAthleteInput = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
`;

const InputWithErrorWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

const InputError = styled('div')`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: -2px;
  font-size: 11px;
  font-family: 'Nunito Sans';
  color: red;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
`;

const SaveButtonWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 16px;
  margin-bottom: 16px;
  padding-top: 18px;
  margin-top: 32px;
`;

const SaveButtonContainerText = styled('div')`
  margin-top: 2px;
  font-weight: 700;
  font-family: 'Nunito Sans';
`;

const PreSaveTableSAQC = ({
  coachCompletion,
  completionOnly,
  contentIndex,
  content,
  localAdditionalInfo,
  localDescription,
  swapDropdownSelectedExercise,
  assignedPace,
  date,
  dayNumber,
}) => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);

  const targetRef = useRef(null);
  const targetRefAthleteInputOnly = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showAthleteInputOnlyTooltip, setShowAthleteInputOnlyTooltip] = useState(false);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');
  const calendarContainerScrollPositionTop = useSelector((state) => state.calendar.data.calendarContainerScrollPositionTop);
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);
  const preSaveTableLocalStateForItem = useSelector(
    (state) => state.calendar.data.preSaveTableLocalState[content.assignedId],
  );
  const [isAdding, setIsAdding] = useState(false);
  const [setNumberValue, setSetNumberValue] = useState(1);
  const [repsInputValue, setRepsInputValue] = useState(''); // Track the raw reps input string
  const [prescribedValueInputValue, setPrescribedValueInputValue] = useState('');
  const [restPerRepInputValue, setRestPerRepInputValue] = useState('');
  const [intensityInputValue, setIntensityInputValue] = useState('');

  const [exerciseData, setExerciseData] = useState({
    reps: [],
  });

  const [errors, setErrors] = useState({
    reps: null,
    prescribedValue: null,
    restPerRep: null,
    intensity: null,
  });
  const setFieldError = useCallback((field, msg) => {
    setErrors((prev) => ({ ...prev, [field]: msg }));
  }, []);
  const errorTooltipId = `saqc-presave-error-tooltip-${content.assignedId}`;

  /** remove athlete input and prescribed value from initial state of inputs if
   * exercie is completion only
   */
  const primaryRowStateInitialStateIfExerciseIsCompletionOnly = [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'reps' },
    { name: 'rest per rep (secs)' },
    { name: 'intensity (bpm)' },
    { name: 'add-property', label: 'Add Property' },
  ];

  const [primaryRows, setPrimaryRows] = useState(content?.inputType === 'C' ? primaryRowStateInitialStateIfExerciseIsCompletionOnly : [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'athlete input', label: `${capitalizeWords(content?.inputTypeDescription)} (${content.unit === 'MS' ? 'm:s' : content.unitAbbr})` },
    { name: 'prescribed value', label: 'Prescribed Value' },
    { name: 'reps' },
    { name: 'rest per rep (secs)' },
    { name: 'intensity (bpm)' },
    { name: 'add-property', label: 'Add Property' },
  ]);

  /** remove athlete input and prescribed value from initial state of switch options if
   * exercie is completion only
   */
  const primaryRowSwitchStateInitialStateIfExerciseIsCompletionOnly = {
    sets: true,
    rest: false,
    reps: false,
    'rest per rep (secs)': false,
    'intensity (bpm)': false,
    'add-property': true,
  };

  const [primaryRowSwitch, setPrimaryRowSwitch] = useState(content?.inputType === 'C' ? primaryRowSwitchStateInitialStateIfExerciseIsCompletionOnly : {
    sets: true,
    rest: false,
    'prescribed value': false,
    reps: false,
    'rest per rep (secs)': false,
    'intensity (bpm)': false,
    'athlete input': true,
    'add-property': true,
  });

  // Turn off 'add-property' when all optional properties are enabled
  useEffect(() => {
    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
        && primaryRowSwitch['prescribed value']
        && primaryRowSwitch['intensity (bpm)']
        && primaryRowSwitch.reps;

    if (allOptionalPropertiesEnabled && primaryRowSwitch['add-property']) {
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': false,
      }));
    } else if (!allOptionalPropertiesEnabled && !primaryRowSwitch['add-property']) {
      // Re-enable add-property if any optional property is turned off
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': true,
      }));
    }
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch['prescribed value'],
    primaryRowSwitch['rest per rep (secs)'],
    primaryRowSwitch['rest per rep (bpm)'],
    primaryRowSwitch['intensity (bpm)'],
    primaryRowSwitch.reps,
  ]);

  const setPrimaryRowSwitchState = (newState) => {
    setPrimaryRowSwitch(newState);
  };

  const [rest, setRest] = useState(null);
  const [intensityUnit, setIntensityUnit] = useState(null);
  const [intensityValue, setIntensityValue] = useState(null);
  const [prescribedValue, setPrescribedValue] = useState(null);
  const [restPerRep, setRestPerRep] = useState(null);
  const [restPerRepUnit, setRestPerRepUnit] = useState(null);

  /** This useEffect detects if the user chooses a different exercise
   * from the item dropdownn select and updates the pre saved item in redux
   * so that the table structure and content updates to match the selected exercise
   */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        ...swapDropdownSelectedExercise,
        title: swapDropdownSelectedExercise.name,
        exerciseId: parseInt(swapDropdownSelectedExercise.id, 10),
        id: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        assignedId: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        preSave: true,
      };
      if (programType === 'free-form-program') {
        dispatch(updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex));
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
      }
    }
  }, [swapDropdownSelectedExercise]);

  useEffect(() => {
    if (!primaryRowSwitch['prescribed value']) {
      setPrescribedValue(null);
      setPrescribedValueInputValue('');
    }
    if (!primaryRowSwitch['intensity (bpm)'] && !primaryRowSwitch['intensity (RPE)'] && !primaryRowSwitch['intensity (%)'] && !primaryRowSwitch['intensity (W)']) {
      setIntensityUnit(null);
      setIntensityValue(null);
      setIntensityInputValue('');
    }
    if (!primaryRowSwitch.rest) {
      setRest(null);
    }
    if (!primaryRowSwitch['rest per rep (secs)'] && !primaryRowSwitch['rest per rep (bpm)']) {
      setRestPerRep(null);
      setRestPerRepInputValue('');
      setRestPerRepUnit(null);
    }
    if (!primaryRowSwitch.reps) {
      const newExerciseData = { ...exerciseData };
      newExerciseData.reps = 0;
      setExerciseData(newExerciseData);
    }
  }, [primaryRowSwitch]);

  // Initialize state from Redux backup if it exists
  // Only runs on mount or when switching to a different item (not on every backup change)
  useEffect(() => {
    const backup = preSaveTableLocalStateForItem;
    if (backup) {
      if (backup.setNumberValue !== undefined) setSetNumberValue(backup.setNumberValue);
      if (backup.rest !== undefined) setRest(backup.rest);
      if (backup.intensityUnit !== undefined) setIntensityUnit(backup.intensityUnit);
      if (backup.intensityValue !== undefined) setIntensityValue(backup.intensityValue);
      if (backup.prescribedValue !== undefined) setPrescribedValue(backup.prescribedValue);
      if (backup.restPerRep !== undefined) setRestPerRep(backup.restPerRep);
      if (backup.restPerRepUnit !== undefined) setRestPerRepUnit(backup.restPerRepUnit);
      if (backup.repsInputValue !== undefined) setRepsInputValue(backup.repsInputValue);
      if (backup.prescribedValueInputValue !== undefined) setPrescribedValueInputValue(backup.prescribedValueInputValue);
      if (backup.restPerRepInputValue !== undefined) setRestPerRepInputValue(backup.restPerRepInputValue);
      if (backup.intensityInputValue !== undefined) setIntensityInputValue(backup.intensityInputValue);
      if (backup.exerciseData !== undefined) setExerciseData(backup.exerciseData);
      if (backup.primaryRowSwitch !== undefined) setPrimaryRowSwitch(backup.primaryRowSwitch);
      if (backup.primaryRows !== undefined) setPrimaryRows(backup.primaryRows);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.assignedId]);

  // Save current state to Redux backup on blur
  const saveStateToRedux = useCallback(() => {
    const stateObject = {
      setNumberValue,
      rest,
      intensityUnit,
      intensityValue,
      prescribedValue,
      restPerRep,
      restPerRepUnit,
      repsInputValue,
      prescribedValueInputValue,
      restPerRepInputValue,
      intensityInputValue,
      exerciseData,
      primaryRowSwitch,
      primaryRows,
    };
    dispatch(setPreSaveTableLocalState(content.assignedId, stateObject));
  }, [
    setNumberValue, rest, intensityUnit, intensityValue, prescribedValue,
    restPerRep, restPerRepUnit, repsInputValue, prescribedValueInputValue,
    restPerRepInputValue, intensityInputValue, exerciseData,
    primaryRowSwitch, primaryRows, content.assignedId, dispatch,
  ]);

  // Track if we've completed initial mount/restore cycle
  const hasInitialized = useRef(false);

  // Auto-save state to Redux on every change (not just blur)
  // This ensures state is preserved when component re-renders (e.g., superset selection)
  useEffect(() => {
    // Skip the initial render cycle - only save after user makes changes
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }
    saveStateToRedux();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setNumberValue,
    rest,
    intensityUnit,
    intensityValue,
    prescribedValue,
    restPerRep,
    restPerRepUnit,
    prescribedValueInputValue,
    restPerRepInputValue,
    intensityInputValue,
    exerciseData,
    primaryRowSwitch,
    primaryRows,
  ]);

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
  };

  const handleInputChange = (field, value) => {
    if (field === 'reps') {
      // Update the raw input value for reps field
      setRepsInputValue(value);

      // Check for partial "custom" input
      const trimmedValue = value.trim().toLowerCase();
      if (['c', 'cu', 'cus', 'cust'].includes(trimmedValue)) {
        setFieldError('reps', 'If you would like to program multiple sets, please enter them as comma separated values, e.g. 12,10,8 to match the number of sets');
        return;
      }

      // Prevent negative values for reps field
      if (value && value.toUpperCase() === 'AMAP') {
        // AMAP is valid, continue
      } else if (value && value.includes('-') && !value.startsWith('-')) {
        // Check for ranges (e.g., "8-12") but not negative numbers
        const parts = value.split('-');
        if (parts.length === 2) {
          const start = parseInt(parts[0], 10);
          const end = parseInt(parts[1], 10);
          if (start < 0 || end < 0) {
            return; // Prevent negative values in ranges
          }
        }
      } else if (value && value.includes('+') && !value.startsWith('-')) {
        // Check for minimum values (e.g., "8+") but not negative numbers
        const numPart = value.replace('+', '');
        if (parseInt(numPart, 10) < 0) {
          return; // Prevent negative minimum values
        }
      } else if (value && !Number.isNaN(Number(value))) {
        // Check for simple numbers (including negative ones)
        if (parseFloat(value) < 0) {
          return; // Prevent negative simple numbers
        }
      }

      const repsArray = value
        .split(',')
        .map((rep) => rep.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10); // Number of sets

      const { isValid: allValid, values: normalizedReps, invalidValues } = normalizeRepArray(repsArray, ['AMAP']);

      if (!allValid) {
        if (invalidValues.length === 1) {
          setFieldError('reps', `Invalid format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
          setFieldError('reps', `Invalid format for values: ${invalidList}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        }
        return;
      }

      // Store the normalized array (case-fixed for AMAP); fill normalization happens on blur
      setExerciseData((prev) => ({
        ...prev,
        reps: normalizedReps,
      }));

      if (normalizedReps.length !== setCount && normalizedReps.length !== 1 && normalizedReps.length > 0) {
        setFieldError('reps', `Expected ${setCount} values or 1 value (to apply to all sets), but received ${normalizedReps.length} values.`);
      } else {
        setFieldError('reps', null);
      }
    } else if (field === 'sets') {
      // Handle sets field change and re-validate reps
      setSetNumberValue(value);

      // Re-validate existing reps against new set count
      if (exerciseData.reps && exerciseData.reps.length > 0) {
        const newSetCount = parseInt(value, 10);
        const repsArray = exerciseData.reps;

        // Check if reps count matches new set count
        if (repsArray.length !== newSetCount && repsArray.length !== 1 && repsArray.length > 0) {
          setFieldError('reps', 'Enter a number for reps to apply to all sets.');
        } else {
          setFieldError('reps', null);
        }
      }

      // Re-validate CSV fields against new set count
      const newSetCount = parseInt(value, 10);
      [
        { input: prescribedValueInputValue, field: 'prescribedValue', label: 'prescribed value' },
        { input: restPerRepInputValue, field: 'restPerRep', label: 'rest per rep' },
        { input: intensityInputValue, field: 'intensity', label: 'intensity' },
      ].forEach(({ input, field: fieldKey, label }) => {
        if (input) {
          const arr = input.split(',').map((v) => v.trim()).filter(Boolean);
          if (arr.length !== newSetCount && arr.length !== 1 && arr.length > 0) {
            setFieldError(fieldKey, `Enter a number for ${label} to apply to all sets.`);
          } else {
            setFieldError(fieldKey, null);
          }
        }
      });
    } else {
      // Update other fields normally
      setExerciseData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const handleRepsBlur = () => {
    if (Array.isArray(exerciseData.reps)) {
      const setCount = parseInt(setNumberValue, 10);

      const { isValid: allValid, values: normalizedTokens, invalidValues } = normalizeRepArray(exerciseData.reps, ['AMAP']);

      if (!allValid) {
        if (invalidValues.length === 1) {
          setFieldError('reps', `Invalid format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('reps', `Invalid format for values: ${invalidList}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (normalizedTokens.length === 1) {
        const filledReps = Array(setCount).fill(normalizedTokens[0]);
        setExerciseData((prev) => ({
          ...prev,
          reps: filledReps,
        }));
        // Update the input display to show the normalized version
        setRepsInputValue(filledReps.join(', '));
        setFieldError('reps', null);
      } else if (normalizedTokens.length !== setCount) {
        setFieldError('reps', `Expected ${setCount} values or 1 value (to apply to all sets), but have ${normalizedTokens.length} values.`);
      } else {
        setExerciseData((prev) => ({
          ...prev,
          reps: normalizedTokens,
        }));
        setRepsInputValue(normalizedTokens.join(', '));
        setFieldError('reps', null);
      }
    } else {
      setFieldError('reps', `Invalid input type. Expected comma-separated values but received: ${typeof exerciseData.reps}.`);
    }
  };

  const handlePrescribedValueInputChange = (value) => {
    setPrescribedValueInputValue(value);
    const valArray = value.split(',').map((v) => v.trim()).filter(Boolean);
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];
    valArray.forEach((v, index) => {
      if (!isValidRangeToken(v)) {
        invalidValues.push({ value: v, position: index + 1 });
      }
    });
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('prescribedValue', `Invalid prescribed value: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      } else {
        const list = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('prescribedValue', `Invalid prescribed values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      }
      return;
    }
    if (valArray.length !== setCount && valArray.length !== 1 && valArray.length > 0) {
      setFieldError('prescribedValue', 'Enter a prescribed value to apply to all sets.');
    } else {
      setFieldError('prescribedValue', null);
    }
  };

  const handlePrescribedValueBlur = () => {
    if (prescribedValueInputValue && prescribedValueInputValue.trim() !== '') {
      const valArray = prescribedValueInputValue.split(',').map((v) => v.trim()).filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);
      const invalidValues = [];
      valArray.forEach((v, index) => {
        if (!isValidRangeToken(v)) {
          invalidValues.push({ value: v, position: index + 1 });
        }
      });
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('prescribedValue', `Invalid prescribed value: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        } else {
          const list = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('prescribedValue', `Invalid prescribed values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        }
        return;
      }
      if (valArray.length === 1) {
        const normalized = Array(setCount).fill(valArray[0]);
        setPrescribedValueInputValue(normalized.join(', '));
        setFieldError('prescribedValue', null);
      } else if (valArray.length === setCount) {
        setFieldError('prescribedValue', null);
      } else {
        setFieldError('prescribedValue', `Expected ${setCount} prescribed values or 1 value (to apply to all sets), but have ${valArray.length} values.`);
      }
    } else {
      setFieldError('prescribedValue', null);
    }
  };

  const handleRestPerRepInputChange = (value) => {
    setRestPerRepInputValue(value);
    const valArray = value.split(',').map((v) => v.trim()).filter(Boolean);
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];
    valArray.forEach((v, index) => {
      if (!isValidRangeToken(v)) {
        invalidValues.push({ value: v, position: index + 1 });
      }
    });
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('restPerRep', `Invalid rest per rep: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      } else {
        const list = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('restPerRep', `Invalid rest per rep values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      }
      return;
    }
    if (valArray.length !== setCount && valArray.length !== 1 && valArray.length > 0) {
      setFieldError('restPerRep', 'Enter a rest per rep value to apply to all sets.');
    } else {
      setFieldError('restPerRep', null);
    }
  };

  const handleRestPerRepBlur = () => {
    if (restPerRepInputValue && restPerRepInputValue.trim() !== '') {
      const valArray = restPerRepInputValue.split(',').map((v) => v.trim()).filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);
      const invalidValues = [];
      valArray.forEach((v, index) => {
        if (!isValidRangeToken(v)) {
          invalidValues.push({ value: v, position: index + 1 });
        }
      });
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('restPerRep', `Invalid rest per rep: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        } else {
          const list = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('restPerRep', `Invalid rest per rep values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        }
        return;
      }
      if (valArray.length === 1) {
        const normalized = Array(setCount).fill(valArray[0]);
        setRestPerRepInputValue(normalized.join(', '));
        setFieldError('restPerRep', null);
      } else if (valArray.length === setCount) {
        setFieldError('restPerRep', null);
      } else {
        setFieldError('restPerRep', `Expected ${setCount} rest per rep values or 1 value (to apply to all sets), but have ${valArray.length} values.`);
      }
    } else {
      setFieldError('restPerRep', null);
    }
  };

  const handleIntensityInputChange = (value) => {
    setIntensityInputValue(value);
    const valArray = value.split(',').map((v) => v.trim()).filter(Boolean);
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];
    valArray.forEach((v, index) => {
      if (!isValidRangeToken(v)) {
        invalidValues.push({ value: v, position: index + 1 });
      }
    });
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('intensity', `Invalid intensity: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      } else {
        const list = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('intensity', `Invalid intensity values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      }
      return;
    }
    if (valArray.length !== setCount && valArray.length !== 1 && valArray.length > 0) {
      setFieldError('intensity', 'Enter an intensity value to apply to all sets.');
    } else {
      setFieldError('intensity', null);
    }
  };

  const handleIntensityBlur = () => {
    if (intensityInputValue && intensityInputValue.trim() !== '') {
      const valArray = intensityInputValue.split(',').map((v) => v.trim()).filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);
      const invalidValues = [];
      valArray.forEach((v, index) => {
        if (!isValidRangeToken(v)) {
          invalidValues.push({ value: v, position: index + 1 });
        }
      });
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('intensity', `Invalid intensity: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        } else {
          const list = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('intensity', `Invalid intensity values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        }
        return;
      }
      if (valArray.length === 1) {
        const normalized = Array(setCount).fill(valArray[0]);
        setIntensityInputValue(normalized.join(', '));
        setFieldError('intensity', null);
      } else if (valArray.length === setCount) {
        setFieldError('intensity', null);
      } else {
        setFieldError('intensity', `Expected ${setCount} intensity values or 1 value (to apply to all sets), but have ${valArray.length} values.`);
      }
    } else {
      setFieldError('intensity', null);
    }
  };

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const handleSaveCalendarExercise = (contentIndexParam) => {
    // Defense-in-depth: re-normalize and validate reps right before submission so no
    // case-drifted ("Amap") or invalid ("AMRAP") tokens can leak past the errors gate.
    let normalizedRepsForSubmission = exerciseData.reps;
    if (Array.isArray(exerciseData.reps) && exerciseData.reps.length > 0) {
      const { isValid: repsAreValid, values: submissionReps } = normalizeRepArray(
        exerciseData.reps,
        ['AMAP'],
      );
      if (!repsAreValid) {
        showErrorToast('Invalid reps value. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".');
        return;
      }
      normalizedRepsForSubmission = submissionReps;
    }

    const setSubmissionArray = [];
    setIsAdding(true);

    // Parse CSV inputs into normalized arrays
    const setCount = parseInt(setNumberValue, 10);
    const parseCsv = (input) => {
      if (!input || !input.trim()) return null;
      const arr = input.split(',').map((v) => v.trim()).filter(Boolean);
      if (arr.length === 1) return Array(setCount).fill(arr[0]);
      return arr;
    };
    const prescribedValues = parseCsv(prescribedValueInputValue);
    const restPerRepValues = parseCsv(restPerRepInputValue);
    const intensityValues = parseCsv(intensityInputValue);

    for (let i = 0; i < setNumberValue; i += 1) {
      let repsValue;
      if (normalizedRepsForSubmission) {
        if (Array.isArray(normalizedRepsForSubmission)) {
          repsValue = normalizedRepsForSubmission[i]
            || normalizedRepsForSubmission[normalizedRepsForSubmission.length - 1]
            || 0;
        } else if (typeof normalizedRepsForSubmission === 'string') {
          repsValue = normalizedRepsForSubmission;
        } else {
          repsValue = 0;
        }
      }
      const setObject = {
        setNumber: i + 1,
      };
      if (repsValue) {
        setObject.reps = repsValue;
      }
      if (prescribedValues) {
        setObject.prescribedValue = toFieldValue(prescribedValues[i] || prescribedValues[prescribedValues.length - 1]);
      }
      if (intensityValues) {
        setObject.intensityValue = toFieldValue(intensityValues[i] || intensityValues[intensityValues.length - 1]);
      }
      if (restPerRepValues) {
        setObject.restPerRep = toFieldValue(restPerRepValues[i] || restPerRepValues[restPerRepValues.length - 1]);
      }
      setSubmissionArray.push(setObject);
    }
    const submissionObject = {
      coachCompletion,
      completionOnly,
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
          programType === 'free-form-program'
            ? dayNumber
            : moment(effectiveDate).format('YYYY-MM-DD'),
      exerciseId: content.exerciseId,
      repIntensityUnit: null,
      repRestUnit: null,
      type: typeFormatter(content.typeDescription),
      sets: setSubmissionArray,
      showSessionBreakItems: false,
    };
    if (localDescription || content.description) {
      submissionObject.description = localDescription || content.description;
    }
    if (localAdditionalInfo || content.additionalInfo) {
      submissionObject.additionalInfo = localAdditionalInfo || content.additionalInfo;
    }
    if (rest) {
      submissionObject.restTime = rest;
    }
    if (intensityUnit) {
      submissionObject.repIntensityUnit = intensityUnit;
    }
    if (restPerRepUnit) {
      submissionObject.repRestUnit = restPerRepUnit;
    }
    if (assignedPace) {
      submissionObject.pacing = assignedPace;
    }
    if (content.groupingLetter) {
      submissionObject.groupingLetter = content.groupingLetter;
    }
    const formattedDate = moment(effectiveDate).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        errorAddReset,
        {
          onSuccess: () => {
          // Trigger focus to next preSave item
            dispatch(focusNextPreSaveProgramItem(contentIndexParam, dayNumber));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        errorAddReset,
        {
          onSuccess: () => {
          // Trigger focus to next preSave item
            dispatch(focusNextPreSaveItem(contentIndexParam, effectiveDate));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    }
  };

  const rowValueDecider = (name, shouldShimmer) => {
    if (shouldShimmer) {
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <RowValue
            type='number'
            value={rest}
            onChange={(e) => setRestFunction(e.target.value)}
            onBlur={saveStateToRedux}
            onKeyDown={(e) => {
              if (e.key === '-') {
                e.preventDefault();
              }
            }}
            background='#FCFCFC'
            data-focus-target={
              (name === 'rest' && primaryRowSwitch.rest && !primaryRowSwitch.sets && !primaryRowSwitch.reps)
                ? content.assignedId
                : undefined
            }
          />
        </RowValueContainer>
      );
    }
    if (name === 'prescribed value') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              isError={!!errors.prescribedValue}
              type='text'
              value={prescribedValueInputValue}
              onChange={(e) => handlePrescribedValueInputChange(e.target.value)}
              onBlur={() => {
                handlePrescribedValueBlur();
                saveStateToRedux();
              }}
              background='#FCFCFC'
              data-focus-target={
                (name === 'prescribed value' && primaryRowSwitch['prescribed value'] && !primaryRowSwitch.sets && !primaryRowSwitch.reps && !primaryRowSwitch.rest)
                  ? content.assignedId
                  : undefined
              }
            />
            {errors.prescribedValue && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.prescribedValue}
              >
                {errors.prescribedValue}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'athlete input') {
      return (
        <RowValueContainer>
          <RowValue
            placeholder='-'
            /** disable the ability to edit this field */
            disabled
            background='#FCFCFC'
            textColor='#BDBDBD'
          />
        </RowValueContainer>
      );
    }
    if (name === 'rest per rep (secs)' || name === 'rest per rep (bpm)') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              isError={!!errors.restPerRep}
              type='text'
              value={restPerRepInputValue}
              background='#FCFCFC'
              onChange={(e) => handleRestPerRepInputChange(e.target.value)}
              onBlur={() => {
                handleRestPerRepBlur();
                saveStateToRedux();
              }}
              data-focus-target={
                ((primaryRowSwitch['rest per rep (secs)'] || primaryRowSwitch['rest per rep (bpm)']) && !primaryRowSwitch.sets && !primaryRowSwitch.reps && !primaryRowSwitch.rest && !primaryRowSwitch['prescribed value'])
                  ? content.assignedId
                  : undefined
              }
            />
            {errors.restPerRep && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.restPerRep}
              >
                {errors.restPerRep}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'intensity (bpm)' || name === 'intensity (RPE)' || name === 'intensity (%)' || name === 'intensity (W)') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              isError={!!errors.intensity}
              type='text'
              value={intensityInputValue}
              onChange={(e) => handleIntensityInputChange(e.target.value)}
              onBlur={() => {
                handleIntensityBlur();
                saveStateToRedux();
              }}
              background='#FCFCFC'
              data-focus-target={
                ((name === 'intensity (bpm)' || name === 'intensity (RPE)' || name === 'intensity (%)' || name === 'intensity (W)')
                  && (primaryRowSwitch['intensity (bpm)'] || primaryRowSwitch['intensity (RPE)'] || primaryRowSwitch['intensity (%)'] || primaryRowSwitch['intensity (W)'])
                  && !primaryRowSwitch.sets && !primaryRowSwitch.reps && !primaryRowSwitch.rest && !primaryRowSwitch['prescribed value'] && !primaryRowSwitch['rest per rep (secs)'] && !primaryRowSwitch['rest per rep (bpm)'])
                  ? content.assignedId
                  : undefined
              }
            />
            {errors.intensity && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.intensity}
              >
                {errors.intensity}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <InputWithErrorWrapper>
          <RowValue
            isError={name === 'reps' && !!errors.reps}
            type='text'
            background='#FCFCFC'
            value={name === 'reps' ? repsInputValue : undefined}
            onChange={(e) => handleInputChange(name, e.target.value)}
            onBlur={name === 'reps' ? () => {
              handleRepsBlur();
              saveStateToRedux();
            } : saveStateToRedux}
            onKeyDown={(e) => {
              // For reps, prevent minus at the beginning
              if (name === 'reps' && e.key === '-' && e.target.selectionStart === 0) {
                e.preventDefault();
              }
              // If the user is mutating an AMAP token they're sitting in the
              // middle of, block the keystroke and restore — except Backspace,
              // which deletes the whole token (opt-in via backspaceDeletes).
              if (name === 'reps') {
                const { value, selectionStart, selectionEnd } = e.target;
                const wipeResult = wipeRepKeywordOnMutation(e, value, selectionStart, selectionEnd, ['AMAP'], { backspaceDeletes: true });
                if (wipeResult.wiped) {
                  e.preventDefault();
                  setRepsInputValue(wipeResult.newValue);
                  handleInputChange('reps', wipeResult.newValue);
                  const inputEl = e.target;
                  requestAnimationFrame(() => {
                    inputEl.setSelectionRange(wipeResult.newCursor, wipeResult.newCursor);
                  });
                }
              }
            }}
            data-focus-target={
              // Add focus target to the first editable field that's visible (sets field takes priority)
              (name === 'reps' && primaryRowSwitch.reps && !primaryRowSwitch.sets)
                ? content.assignedId
                : undefined
            }
          />
          {name === 'reps' && errors.reps && (
            <InputError
              data-tooltip-id={errorTooltipId}
              data-tooltip-content={errors.reps}
            >
              {errors.reps}
            </InputError>
          )}
        </InputWithErrorWrapper>
      </RowValueContainer>
    );
  };

  const updateColumnName = useCallback((oldName, newName) => {
    // Prevent updating the add-property column
    if (oldName === 'add-property' || newName === 'add-property') return;

    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.unitAbbr) {
      return `(${unitContent?.unitAbbr === 'wgt' ? 'lbs' : unitContent?.unitAbbr})`;
    }
    return '';
  };

  // This useEffect is responsible for managing focus on input fields when the focusTarget changes.
  // It checks if the current focusTarget's assignedId matches the content's assignedId.
  // If they match, it finds the first input element with a data-focus-target attribute equal to the content's assignedId.
  // Once found, it sets a timeout to focus on this input element after a short delay, ensuring the DOM is ready.
  useEffect(() => {
    if (focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [focusTarget, content.assignedId]);

  return (
    <FormWrapper
      ref={tableContainerRef}
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveCalendarExercise(contentIndex);
      }}
    >
      <Container>
        <PrimaryRow isAdding={isAdding}>
          {primaryRows.map((primaryRow) => (
            primaryRowSwitch[primaryRow.name] ? (
              primaryRow.name === 'sets' ? !isAdding ? <SetPrimaryValueContainer>{capitalizeWords(primaryRow.name)}</SetPrimaryValueContainer> : <Shimmer><SetPrimaryValueContainer shimmer>{primaryRow.name}</SetPrimaryValueContainer></Shimmer> : primaryRow.name === 'add-property' ? (
                !isAdding ? (
                  <PrimaryValueContainer>
                    <AddPropertyColumnComponent
                      mode='header'
                      primaryRows={primaryRows}
                      primaryRowSwitch={primaryRowSwitch}
                      setPrimaryRowSwitch={setPrimaryRowSwitch}
                      setPrimaryRowSwitchState={setPrimaryRowSwitchState}
                      containerRef={tableContainerRef}
                      assignedId={content.assignedId}
                    />
                  </PrimaryValueContainer>
                ) : (
                  <Shimmer>
                    <PrimaryValueContainer shimmer>
                      <PrimaryRowValue isAdding={isAdding}>
                        Add Property
                      </PrimaryRowValue>
                    </PrimaryValueContainer>
                  </Shimmer>
                )
              ) : (
                !isAdding ? (
                  <PrimaryValueContainer>
                    <PrimaryRowValue isAdding={isAdding}>
                      {(primaryRow.name === 'reps') || (primaryRow.name === 'athlete input') ? (
                        <NameAndDropdown
                          ref={primaryRow.name === 'reps' ? targetRef : targetRefAthleteInputOnly}
                        >
                          {(primaryRow.name === 'reps' || (primaryRow.name === 'athlete input' && !completionOnly)) && (
                            <InfoIconWrapper>
                              <IcomoonReact
                                iconSet={iconSet}
                                size={14}
                                icon='info2'
                                color='rgba(189, 189, 189, 1)'
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={() => {
                                  if (primaryRow.name === 'reps') {
                                    setShowTooltip(true);
                                  }
                                  if (primaryRow.name === 'athlete input' && !completionOnly) {
                                    setShowAthleteInputOnlyTooltip(true);
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (primaryRow.name === 'reps') {
                                    setShowTooltip(false);
                                  }
                                  if (primaryRow.name === 'athlete input') {
                                    setShowAthleteInputOnlyTooltip(false);
                                  }
                                }}
                              />
                            </InfoIconWrapper>
                          )}
                          {primaryRow.name === 'reps' && showTooltip && (
                          <TBportalTooltip targetRef={targetRef} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
                            <TooltipDataContainer>
                              <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
                              <ul style={{ paddingLeft: '20px' }}>
                                <li>
                                  <Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 5)</Text>
                                </li>
                                <li><Text fontSize='13px' fontWeight={400} color='white'>Comma Separated (e.g. 12,10,8)</Text></li>
                                <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 8-12)</Text></li>
                                <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 8+)</Text></li>
                                <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;AMAP&quot; for as many as possible</Text></li>
                              </ul>
                            </TooltipDataContainer>
                          </TBportalTooltip>
                          )}
                          {primaryRow.name === 'athlete input' && showAthleteInputOnlyTooltip && !completionOnly && (
                          <TBportalTooltip targetRef={targetRefAthleteInputOnly} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowAthleteInputOnlyTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
                            <TooltipDataContainerAthleteInput>
                              <Text fontSize='13px' fontWeight={400} color='white'>Athlete input only</Text>
                            </TooltipDataContainerAthleteInput>
                          </TBportalTooltip>
                          )}
                          <SwapPropDropdownSAQC
                        /** always visible except for athlete input, coach should not be able to edit athlete input */
                            visible={primaryRow.name !== 'athlete input'}
                            setPrimaryRowSwitch={setPrimaryRowSwitch}
                            primaryRowSwitch={primaryRowSwitch}
                            rowName={primaryRow.name}
                            size={12}
                            updateColumnName={updateColumnName}
                            setIntensityUnit={setIntensityUnit}
                            setRestPerRepUnit={setRestPerRepUnit}
                            name={(primaryRow?.label || capitalizeWords(primaryRow.name))}
                            unit={primaryRow.name === 'prescribed value' ? findCorrectUnit(content) : null}
                          />
                        </NameAndDropdown>
                      ) : (
                        <NameAndDropdown>
                          <SwapPropDropdownSAQC
                          /** always visible except for athlete input, coach should not be able to edit athlete input */
                            visible={primaryRow.name !== 'athlete input'}
                            setPrimaryRowSwitch={setPrimaryRowSwitch}
                            primaryRowSwitch={primaryRowSwitch}
                            rowName={primaryRow.name}
                            size={12}
                            updateColumnName={updateColumnName}
                            setIntensityUnit={setIntensityUnit}
                            setRestPerRepUnit={setRestPerRepUnit}
                            name={(primaryRow?.label || capitalizeWords(primaryRow.name))}
                            unit={primaryRow.name === 'prescribed value' ? findCorrectUnit(content) : null}
                          />
                        </NameAndDropdown>
                      )}
                    </PrimaryRowValue>
                  </PrimaryValueContainer>
                ) : (
                  <Shimmer>
                    <PrimaryValueContainer shimmer>
                      <PrimaryRowValue isAdding={isAdding}>
                        <NameAndDropdown>
                          {primaryRow.name === 'prescribed value' ? capitalizeWords('Prescribed Value') : capitalizeWords(primaryRow.name)}
                          {' '}
                          {primaryRow.name === 'prescribed value' ? findCorrectUnit(content) : null}
                        </NameAndDropdown>
                      </PrimaryRowValue>
                    </PrimaryValueContainer>
                  </Shimmer>
                )
              )
            ) : null
          ))}
          {/* {!isAdding ? (
            <div style={{
              minWidth: 56,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
            >
              <StaticAddColumnDropdownComponent
                primaryRows={primaryRows}
                primaryRowSwitch={primaryRowSwitch}
                setPrimaryRowSwitch={setPrimaryRowSwitch}
                size={12}
                setIntensityUnit={setIntensityUnit}
                setPrimaryRowSwitchState={setPrimaryRowSwitchState}
                assignedId={content.assignedId}
              >
                <AddColumnButtonContainer className='add-column-button'>
                  <AddColumnIcon>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={12}
                      icon='plus'
                      color='white'
                    />
                  </AddColumnIcon>
                </AddColumnButtonContainer>
              </StaticAddColumnDropdownComponent>
            </div>
          ) : (
            <div style={{
              minWidth: 56,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
            >
              <Shimmer>
                <div style={{
                  minHeight: 24,
                  minWidth: 24,
                  borderRadius: 3,
                }}
                />
              </Shimmer>
            </div>
          )} */}
        </PrimaryRow>
        <ValueRow>
          <ErrorAndRowsContainer>
            {/* <form onSubmit={(e) => {
            e.preventDefault();
            handleSaveCalendarExercise(contentIndex);
          }}
          > */}
            <RowsContainer hasError={Object.values(errors).some(Boolean)}>
              {primaryRows.map((primaryRow) => (
                primaryRowSwitch[primaryRow.name] ? (
                  primaryRow.name === 'sets' ? (
                    !isAdding ? (
                      <SetRowValueContainer>
                        <SetRowValue
                          type='number'
                          value={setNumberValue}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            // Prevent negative values
                            if (value < 0) return;
                            handleInputChange('sets', value);
                          }}
                          onBlur={saveStateToRedux}
                          onKeyDown={(e) => {
                            if (e.key === '-') {
                              e.preventDefault();
                            }
                          }}
                          data-focus-target={primaryRowSwitch.sets ? content.assignedId : undefined}
                        />
                      </SetRowValueContainer>
                    ) : (
                      <Shimmer>
                        <SetRowValueContainer shimmer>
                          <ShimmerSetRowValue />
                        </SetRowValueContainer>
                      </Shimmer>
                    )
                  ) : primaryRow.name === 'add-property' ? (
                    !isAdding ? (
                      <RowValueContainer>
                        <AddPropertyColumnComponent
                          mode='input'
                          primaryRows={primaryRows}
                          primaryRowSwitch={primaryRowSwitch}
                          setPrimaryRowSwitch={setPrimaryRowSwitch}
                          setPrimaryRowSwitchState={setPrimaryRowSwitchState}
                          containerRef={tableContainerRef}
                          assignedId={content.assignedId}
                        />
                      </RowValueContainer>
                    ) : (
                      <Shimmer>
                        <RowValueContainer shimmer>
                          <ShimmerRowValue />
                        </RowValueContainer>
                      </Shimmer>
                    )
                  )
                    : rowValueDecider(primaryRow.name, isAdding)
                ) : null
              ))}
              {/* {!isAdding ? (
                <SaveButtonContainer type='submit'>
                  FILL
                  <RemoveIcon>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={12}
                      icon='down-arrow'
                      color='white'
                    />
                  </RemoveIcon>
                </SaveButtonContainer>
              ) : (
                <Shimmer>
                  <SaveButtonContainer shimmer>
                    FILL
                    <RemoveIcon>
                      <IcomoonReact
                        iconSet={iconSet}
                        size={12}
                        icon='down-arrow'
                        color='transparent'
                      />
                    </RemoveIcon>
                  </SaveButtonContainer>
                </Shimmer>
              )} */}
            </RowsContainer>
            {/* </form> */}
          </ErrorAndRowsContainer>
        </ValueRow>
      </Container>
      <SaveButtonWrapper>
        {!isAdding ? (
          <SaveButtonContainer type='submit'>
            <SaveButtonContainerText>
              ADD
            </SaveButtonContainerText>
            <RemoveIcon>
              <IcomoonReact
                iconSet={iconSet}
                size={12}
                icon='down-arrow'
                color='white'
              />
            </RemoveIcon>
          </SaveButtonContainer>
        ) : (
          <Shimmer>
            <SaveButtonContainer shimmer>
              <SaveButtonContainerText>
                ADD
              </SaveButtonContainerText>
              <RemoveIcon>
                <IcomoonReact
                  iconSet={iconSet}
                  size={12}
                  icon='down-arrow'
                  color='transparent'
                />
              </RemoveIcon>
            </SaveButtonContainer>
          </Shimmer>
        )}
      </SaveButtonWrapper>
      <ReactTooltip
        id={errorTooltipId}
        place='top'
        positionStrategy='fixed'
        delayShow={300}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '12px',
          maxWidth: '260px',
          padding: '6px 10px',
          lineHeight: 1.3,
        }}
      />
    </FormWrapper>
  );
};

PreSaveTableSAQC.propTypes = {
  coachCompletion: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  localAdditionalInfo: PropTypes.string,
  localDescription: PropTypes.string,
  assignedPace: PropTypes.string,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
};

PreSaveTableSAQC.defaultProps = {
  localAdditionalInfo: '',
  localDescription: '',
  assignedPace: '',
  date: null,
  dayNumber: null,
};

export default PreSaveTableSAQC;
